import { db } from "@/main";
import { Sensor } from "@/data/models/Sensor";

/**
 * Sensor documents already fetched in this session, keyed by serial.
 *
 * A plain object rather than a `Map` because Vue 2 cannot track `Map` mutations, and `null` marks
 * "looked up, does not exist" so a sensor without a document is not re-queried on every dialog
 * open (49 referenced serials had no document as of 2026-08-26).
 */
const sensorCache: Record<string, Sensor | null> = {};

/**
 * Loads one sensor document, from cache when it has already been read.
 *
 * A one-shot read, not an observer: the SENSOR INFO dialog is transient, and a listener per slot
 * would be up to nine sockets for a few rows of text. Errors resolve to `null` rather than
 * throwing — the dialog falls back to the event 77 snapshot values and stays useful.
 */
export async function getSensor(serialNumber?: string): Promise<Sensor | null> {
  if (!serialNumber) {
    return null;
  }

  if (serialNumber in sensorCache) {
    return sensorCache[serialNumber];
  }

  try {
    const snapshot = await db.collection("sensors").doc(serialNumber).get();
    const sensor = snapshot.exists ? (snapshot.data() as Sensor) : null;

    sensorCache[serialNumber] = sensor;
    return sensor;
  } catch {
    // Read failures are not surfaced: the caller has snapshot values to fall back on, and a
    // missing signal strength is not worth a snackbar. Not cached, so the next open retries.
    return null;
  }
}
